import type { MarkdownDocument } from '@markbird/shared';
import { contentHash } from './folderSync/reconcile';

/**
 * What to do with one document when the local copy, the remote copy and the
 * last state both sides agreed on are known.
 *
 * Sync used to compare only local against remote, which cannot tell "changed
 * here" from "never had it": every start pushed the whole library back up, and
 * every remote arrival that differed was announced as a conflict. Keeping the
 * agreed state per document (see syncState.ts) turns both into ordinary cases,
 * and leaves a real conflict — both sides changed since they last agreed — as
 * the only thing the user is asked about.
 *
 * Content and placement (pinned, folder, rank) are decided separately, because
 * moving a note is not editing it and must not bump `updatedAt`.
 */

/** The last state a document was known to have on both sides. */
export interface SyncedState {
  docId: string;
  /** Hash of the content both sides had. */
  contentHash: string;
  updatedAt: number;
  metaUpdatedAt: number;
  /** Whether that state was a deletion (a tombstone). */
  purged?: boolean;
  /**
   * The content hash this device deleted. A copy that turns up afterwards with
   * different text is writing the deleting device never saw, and comes back.
   */
  purgedHash?: string;
}

/**
 * A document as it travels: the fields of `MarkdownDocument`, plus the marks
 * that make deletion and expiry visible to other devices.
 */
export interface RemoteDoc extends MarkdownDocument {
  /** A tombstone: the document was deleted for good on some device. */
  purged?: boolean;
  /** When the tombstone was written, so a later edit can outrank it. */
  purgedAt?: number;
  /** Firestore TTL field: when the tombstone itself may be removed. */
  expiresAt?: number | null;
}

export type SyncAction =
  /** Nothing to do. */
  | { kind: 'none' }
  /** Write the local copy up. */
  | { kind: 'push' }
  /** Apply `doc` locally (content and/or placement came from the remote copy). */
  | { kind: 'apply'; doc: MarkdownDocument; contentChanged: boolean }
  /**
   * Both sides changed the text since they last agreed. The winner is applied,
   * the loser is preserved in version history — a divergence is never dropped.
   */
  | { kind: 'conflict'; doc: MarkdownDocument; preserve: MarkdownDocument; contentChanged: true }
  /** The document was deleted for good elsewhere; delete it here too. */
  | { kind: 'purge' };

const PLACEMENT = ['pinned', 'folder', 'rank'] as const;

function withPlacement(base: MarkdownDocument, from: MarkdownDocument): MarkdownDocument {
  const merged: MarkdownDocument = { ...base };
  for (const field of PLACEMENT) {
    if (from[field] === undefined) delete merged[field];
    else (merged as unknown as Record<string, unknown>)[field] = from[field];
  }
  merged.metaUpdatedAt = from.metaUpdatedAt;
  if (merged.metaUpdatedAt === undefined) delete merged.metaUpdatedAt;
  return merged;
}

/** The agreed state a document would have if it were synced right now. */
export function stateOf(doc: MarkdownDocument): SyncedState {
  return {
    docId: doc.id,
    contentHash: contentHash(doc.content),
    updatedAt: doc.updatedAt,
    metaUpdatedAt: doc.metaUpdatedAt ?? 0,
  };
}

/** Decide what a document needs, given all three views of it. */
export function decide(
  local: MarkdownDocument | undefined,
  remote: RemoteDoc | undefined,
  agreed: SyncedState | undefined,
): SyncAction {
  if (!local && !remote) return { kind: 'none' };
  if (!remote) {
    // Only here. Either it is new (push it) or it was deleted remotely before
    // tombstones existed — in which case the agreed state says it was synced,
    // and pushing it back is still the safe direction: content is never lost.
    return { kind: 'push' };
  }

  if (remote.purged) {
    // Writing that the deleting device never saw outranks the deletion: the note
    // comes back rather than disappearing under someone else's cleanup. That is
    // either an edit this device has not sent yet, or one made after the
    // deletion was written.
    const unsyncedEdit = local && agreed && contentHash(local.content) !== agreed.contentHash;
    // `>=`, not `>`: when a deletion and an edit land in the same millisecond,
    // the writing wins. A deletion that did not take can be repeated; a
    // paragraph that quietly disappeared cannot be recovered by trying again.
    const editedAfter = local && agreed && local.updatedAt >= (remote.purgedAt ?? remote.updatedAt);
    return unsyncedEdit || editedAfter ? { kind: 'push' } : { kind: 'purge' };
  }

  if (!local) {
    // Not here. Usually new, but this device may be the one that deleted it —
    // then the agreed state remembers the deletion. A copy newer than what we
    // deleted means someone brought it back, and that wins over our tombstone;
    // the same copy we deleted only means the others have not heard yet.
    if (agreed?.purged) {
      const differentText =
        agreed.purgedHash !== undefined && contentHash(remote.content) !== agreed.purgedHash;
      return differentText || remote.updatedAt >= agreed.updatedAt
        ? { kind: 'apply', doc: remote, contentChanged: true }
        : { kind: 'push' };
    }
    return { kind: 'apply', doc: remote, contentChanged: true };
  }

  const localHash = contentHash(local.content);
  const remoteHash = contentHash(remote.content);
  const localMeta = local.metaUpdatedAt ?? 0;
  const remoteMeta = remote.metaUpdatedAt ?? 0;

  // No agreement yet — the first sync on a device, or the first start after the
  // upgrade that introduced them. Identical copies need no writing on either
  // side; they only need to be recorded as agreed, which is what 'none' does.
  if (!agreed && localHash === remoteHash && localMeta === remoteMeta) return { kind: 'none' };

  // "Different from what we agreed" is not the same as "newer". Two devices
  // writing at once means each briefly sees the other's older version in the
  // shared copy, so versions are ordered — by clock, and by hash when clocks
  // collide. The hash tie-break is arbitrary but identical on both devices,
  // which is what stops them from pushing an older copy at each other for ever.
  const contentAhead = (u: number, hash: string) =>
    !agreed || u > agreed.updatedAt || (u === agreed.updatedAt && hash > agreed.contentHash);
  const contentBehind = (u: number, hash: string) =>
    !!agreed && hash !== agreed.contentHash && !contentAhead(u, hash);

  const localContentChanged = contentAhead(local.updatedAt, localHash);
  const remoteContentChanged = contentAhead(remote.updatedAt, remoteHash);
  const localPlacementChanged = !agreed || localMeta > agreed.metaUpdatedAt;
  const remotePlacementChanged = !agreed || remoteMeta > agreed.metaUpdatedAt;
  const remoteBehind =
    contentBehind(remote.updatedAt, remoteHash) || (!!agreed && remoteMeta < agreed.metaUpdatedAt);

  // Placement: whichever side changed it; if both did, the later clock wins.
  const placementIsRemote =
    localPlacementChanged && remotePlacementChanged
      ? remoteMeta > localMeta
      : remotePlacementChanged;
  const placementFrom = placementIsRemote ? remote : local;

  if (localContentChanged && remoteContentChanged && localHash !== remoteHash) {
    // The same order both devices use above, so both pick the same winner.
    const remoteWins =
      remote.updatedAt > local.updatedAt ||
      (remote.updatedAt === local.updatedAt && remoteHash > localHash);
    const winner = remoteWins ? remote : local;
    return {
      kind: 'conflict',
      doc: withPlacement({ ...winner }, placementFrom),
      preserve: remoteWins ? local : { ...local, content: remote.content },
      contentChanged: true,
    };
  }

  const contentIsRemote = remoteContentChanged;
  const contentFrom = contentIsRemote ? remote : local;
  if (!contentIsRemote && !placementIsRemote) {
    // Nothing came from the remote copy: either we are ahead, or we agree.
    return localContentChanged || localPlacementChanged || remoteBehind
      ? { kind: 'push' }
      : { kind: 'none' };
  }

  const doc = withPlacement({ ...contentFrom }, placementFrom);
  return { kind: 'apply', doc, contentChanged: contentIsRemote && local.content !== doc.content };
}

/** Whether the local copy still has something the remote copy lacks after applying `doc`. */
export function needsPush(doc: MarkdownDocument, remote: RemoteDoc): boolean {
  return (
    doc.updatedAt !== remote.updatedAt ||
    contentHash(doc.content) !== contentHash(remote.content) ||
    (doc.metaUpdatedAt ?? 0) !== (remote.metaUpdatedAt ?? 0) ||
    PLACEMENT.some((f) => (doc[f] ?? null) !== (remote[f] ?? null))
  );
}
